<script setup lang="ts">
import { computed } from 'vue';
import EntityPortrait from './EntityPortrait.vue';
import type { EntitySummary } from '../types/entities';

const props = withDefaults(defineProps<{
  entities: EntitySummary[];
  size?: 'xs' | 'sm' | 'md' | 'lg';
  label?: string;
  showNames?: boolean;
  showEmpty?: boolean;
  interactive?: boolean;
}>(), { size: 'sm', label: 'Figuren', showNames: false, showEmpty: false, interactive: true });
const emit = defineEmits<{ openEntity: [id: string] }>();

const uniqueEntities = computed(() => [...new Map(props.entities.map((entity) => [entity.id, entity])).values()]);
</script>

<template>
  <section v-if="uniqueEntities.length || showEmpty" class="entity-portrait-strip" :class="[`strip-${size}`, { 'with-names': showNames }]" :aria-label="label">
    <span class="entity-portrait-strip-label">{{ label }}</span>
    <div v-if="uniqueEntities.length" class="entity-portrait-strip-items">
      <component :is="interactive ? 'button' : 'span'" v-for="entity in uniqueEntities" :key="entity.id" :type="interactive ? 'button' : undefined" :title="entity.title" @click="interactive && emit('openEntity', entity.id)">
        <EntityPortrait :entity="entity" :size="size" />
        <small v-if="showNames">{{ entity.title }}</small>
      </component>
    </div>
    <small v-else class="entity-portrait-strip-empty">Noch keine Figuren zugeordnet</small>
  </section>
</template>

<style scoped>
.entity-portrait-strip{display:flex;align-items:center;gap:12px;min-width:0;padding:11px 0 14px;border-bottom:1px solid var(--ui-border-subtle)}
.entity-portrait-strip-label{flex:none;color:var(--ui-action-text);font-size:9px;font-weight:850;letter-spacing:.09em;text-transform:uppercase}
.entity-portrait-strip-items{display:flex;align-items:flex-start;flex-wrap:wrap;gap:9px;min-width:0}
.entity-portrait-strip-items>button,.entity-portrait-strip-items>span{display:grid;justify-items:center;gap:6px;min-width:0;padding:0;border:0;color:var(--ui-text-soft);background:transparent;box-shadow:none;text-align:center;transition:none}.entity-portrait-strip-items>button{cursor:pointer}
.entity-portrait-strip-items>button:hover,.entity-portrait-strip-items>button:focus-visible{background:transparent!important;box-shadow:none!important}
.entity-portrait-strip-items>button:hover :deep(.entity-portrait),.entity-portrait-strip-items>button:focus-visible :deep(.entity-portrait){border-color:var(--ui-action);box-shadow:0 0 0 2px var(--ui-surface-2),0 0 16px color-mix(in srgb,var(--ui-action) 48%,transparent)}
.entity-portrait-strip-items small{max-width:92px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:10px;font-weight:700}
.entity-portrait-strip-empty{color:var(--ui-text-muted);font-size:11px;font-style:italic}
.strip-lg{align-items:flex-start;gap:17px;padding:15px 0 20px}.strip-lg .entity-portrait-strip-label{padding-top:25px}.strip-lg .entity-portrait-strip-items{gap:17px}
.strip-md{padding-block:13px 17px}.strip-md .entity-portrait-strip-label{padding-top:18px}
.strip-xs{gap:7px;padding:4px 0;border:0}.strip-xs .entity-portrait-strip-label{display:none}.strip-xs .entity-portrait-strip-items{flex-wrap:nowrap;gap:0}.strip-xs .entity-portrait-strip-items>*+*{margin-left:-5px}
</style>
